import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Home, Tag, Gauge, User, LogIn, ShieldQuestion, Building2, Check, ChevronDown, KeyRound, LifeBuoy, FileLock2 } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { RegionSelector } from "./RegionSelector";
import { LanguageSelector } from "./LanguageSelector";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// Onglets produit : les libellés sont résolus via i18n (clés common:nav.*).
const publicTabs = [
  { key: "home", labelKey: "nav.home", mobileKey: "nav.home", path: "/", testid: "nav-tab-home", icon: Home },
  { key: "generator", labelKey: "nav.generator", mobileKey: "nav.generator", path: "/generateur", testid: "nav-tab-generator", icon: KeyRound },
  { key: "request", labelKey: "nav.request", mobileKey: "nav.requestShort", path: "/demander", testid: "nav-tab-request", icon: ShieldQuestion },
  { key: "pricing", labelKey: "nav.pricing", mobileKey: "nav.pricing", path: "/offres", testid: "nav-tab-pricing", icon: Tag },
  { key: "tester", labelKey: "nav.tester", mobileKey: "nav.testerShort", path: "/tester-mot-de-passe", testid: "nav-tab-tester", icon: ShieldCheck },
  { key: "help", labelKey: "nav.help", mobileKey: "nav.help", path: "/documentation", testid: "nav-tab-help", icon: LifeBuoy },
];

// Onglet réservé aux utilisateurs connectés (émetteur Secure Files obligatoirement authentifié).
const filesTab = { key: "files", labelKey: "nav.files", mobileKey: "nav.filesShort", path: "/fichiers", testid: "nav-tab-files", icon: FileLock2 };

const WorkspaceSwitcher = () => {
  const { t } = useTranslation("common");
  const { workspaces, activeWorkspace, switchWorkspace } = useAuth();
  if (!workspaces || workspaces.length < 2) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid="workspace-switcher-btn"
          variant="outline"
          className="border-slate-200 bg-white hover:bg-slate-100 text-slate-900 max-w-[220px]"
        >
          <Building2 className="w-4 h-4 mr-2 shrink-0 text-emerald-600" />
          <span className="truncate">{activeWorkspace?.name || t("workspace.fallback")}</span>
          <ChevronDown className="w-4 h-4 ml-2 shrink-0 text-slate-400" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" data-testid="workspace-switcher-menu">
        <DropdownMenuLabel>{t("workspace.active")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {workspaces.map((w) => (
          <DropdownMenuItem
            key={w.id}
            data-testid={`workspace-option-${w.id}`}
            onClick={() => switchWorkspace(w.id)}
            className="cursor-pointer"
          >
            <div className="flex items-center justify-between w-full gap-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">
                  {w.is_personal ? t("workspace.mySpace") : w.name}
                </div>
                <div className="text-xs text-slate-400">{t(`roles.${w.role}`, { defaultValue: w.role_label })}</div>
              </div>
              {activeWorkspace?.id === w.id && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { user } = useAuth();

  const isAdmin = user?.role === "admin";
  const tabs = user ? [...publicTabs.slice(0, 3), filesTab, ...publicTabs.slice(3)] : publicTabs;

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <header
      data-testid="nav-header"
      className="sticky top-0 z-30 glass border-b border-slate-200"
    >
      {/* NIVEAU 1 — Identité SecurePass + Compte/Connexion */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-2.5 group"
        >
          <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center group-hover:bg-emerald-500/25 transition-colors">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold tracking-tight text-lg">
              Secure<span className="text-emerald-600">Pass</span>
            </div>
          </div>
        </Link>

        <div className="flex items-center gap-2">
          {user && !isAdmin && <WorkspaceSwitcher />}
          <LanguageSelector />
          {/* Sélecteur de région global — desktop uniquement, immédiatement à gauche du compte */}
          <div className="hidden md:block">
            <RegionSelector variant="desktop" />
          </div>
          {user ? (
            <>
              <Button
                data-testid="nav-account-btn"
                onClick={() => navigate("/compte")}
                variant="outline"
                className="border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 px-2.5 sm:px-4"
              >
                <User className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">{t("account.mySpace")}</span>
              </Button>
              {isAdmin && (
                <Button
                  data-testid="nav-admin-btn"
                  onClick={() => navigate("/admin")}
                  variant="outline"
                  className="border-slate-200 bg-slate-100 hover:bg-slate-200 text-slate-900 px-2.5 sm:px-4"
                >
                  <Gauge className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">{t("account.adminConsole")}</span>
                </Button>
              )}
            </>
          ) : (
            <Button
              data-testid="nav-login-btn"
              onClick={() => navigate("/connexion")}
              variant="outline"
              className="border-slate-200 bg-slate-100 hover:bg-slate-100 text-slate-900"
            >
              <LogIn className="w-4 h-4 mr-2" /> {t("account.login")}
            </Button>
          )}
        </div>
      </div>

      {/* NIVEAU 2 — Navigation produit (desktop, centrée) */}
      <nav
        aria-label={t("nav.ariaMain")}
        className="hidden md:block border-t border-slate-200/60"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex flex-wrap justify-center gap-1">
          {tabs.map((tab) => {
            const active = isActive(tab.path);
            return (
              <button
                key={tab.key}
                data-testid={tab.testid}
                onClick={() => navigate(tab.path)}
                aria-current={active ? "page" : undefined}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${
                  active
                    ? "bg-slate-100 text-slate-900"
                    : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {t(tab.labelKey)}
              </button>
            );
          })}
        </div>
      </nav>

      {/* NIVEAU 2 — Navigation produit (mobile, scrollable) */}
      <nav aria-label={t("nav.ariaMain")} className="md:hidden flex items-center gap-1 px-3 pb-2 overflow-x-auto border-t border-slate-200/60 pt-2">
        {tabs.map((tab) => {
          const active = isActive(tab.path);
          return (
            <button
              key={tab.key}
              data-testid={`${tab.testid}-mobile`}
              onClick={() => navigate(tab.path)}
              aria-current={active ? "page" : undefined}
              className={`flex-1 min-w-max flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium whitespace-nowrap ${
                active ? "bg-slate-100 text-slate-900" : "text-slate-500"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {t(tab.mobileKey)}
            </button>
          );
        })}
      </nav>

      {/* Sélecteur de région (mobile) — intégré au menu mobile pour ne pas surcharger la barre supérieure */}
      <div className="md:hidden px-3 pb-2 border-t border-slate-200/60 pt-2">
        <RegionSelector variant="mobile" />
      </div>
    </header>
  );
};
